import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare, Search } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports.mjs';

const LEGACY_ICON_MAP = {
  '📋': 'ClipboardList',
  '💰': 'Receipt',
  '🏥': 'Hospital',
  '🏠': 'House',
  '💬': 'MessageSquare',
  '🌱': 'Sprout',
  '🧾': 'ReceiptText',
};

const MAX_VISIBLE_ICONS = 80;

const toPascalIconName = (iconName) => iconName
  .split('-')
  .map(part => part.charAt(0).toUpperCase() + part.slice(1))
  .join('');

const ALL_ICON_NAMES = Object.keys(dynamicIconImports)
  .sort((a, b) => a.localeCompare(b))
  .map(toPascalIconName);

export const normalizeLucideIconName = (icon) => LEGACY_ICON_MAP[icon] || icon || 'MessageSquare';

export const toLucideDynamicIconName = (iconName) => (
  normalizeLucideIconName(iconName)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase()
);

export const formatLucideIconName = (name) => toLucideDynamicIconName(name)
  .split('-')
  .map(part => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

export function DynamicLucideIcon({ icon, className }) {
  const [Icon, setIcon] = useState(null);

  useEffect(() => {
    let mounted = true;
    const iconImport = dynamicIconImports[toLucideDynamicIconName(icon)] || dynamicIconImports['message-square'];

    iconImport()
      .then((module) => {
        if (mounted) setIcon(() => module.default);
      })
      .catch(() => {
        if (mounted) setIcon(() => MessageSquare);
      });

    return () => {
      mounted = false;
    };
  }, [icon]);

  const RenderIcon = Icon || MessageSquare;
  return <RenderIcon className={className} />;
}

export function LucideIconPicker({ value, onChange, isOpen, onToggle, onClose, label, testId, inline = false }) {
  const selectedIconName = normalizeLucideIconName(value);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const visibleIconNames = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = normalizedQuery
      ? ALL_ICON_NAMES.filter((iconName) => (
          iconName.toLowerCase().includes(normalizedQuery) ||
          formatLucideIconName(iconName).toLowerCase().includes(normalizedQuery)
        ))
      : ALL_ICON_NAMES;

    return filtered.slice(0, MAX_VISIBLE_ICONS);
  }, [query]);

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={isOpen}
        data-testid={testId}
        className="h-12 w-full justify-start gap-2"
      >
        <DynamicLucideIcon icon={selectedIconName} className="h-4 w-4 text-primary" />
        <span className="truncate text-xs">{formatLucideIconName(selectedIconName)}</span>
      </Button>

      {isOpen && (
        <div className={`${inline ? 'relative mt-2 w-full' : 'absolute left-0 top-full z-30 mt-2 w-[min(26rem,calc(100vw-4rem))]'} rounded-xl border border-border bg-background p-3 shadow-xl`}>
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Choose Icon</p>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search icons..."
              className="pl-9"
              aria-label="Search icons"
            />
          </div>
          <div className={`grid max-h-72 ${inline ? 'grid-cols-2' : 'grid-cols-4 sm:grid-cols-5'} gap-2 overflow-y-auto p-1`}>
            {visibleIconNames.map((iconName) => {
              const selected = iconName === selectedIconName;
              return (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => {
                    onChange(iconName);
                    onClose();
                  }}
                  title={formatLucideIconName(iconName)}
                  className={`flex h-14 flex-col items-center justify-center gap-1 rounded-lg border text-[10px] transition-colors ${
                    selected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  <DynamicLucideIcon icon={iconName} className="h-5 w-5" />
                  <span className="max-w-full truncate px-1">{formatLucideIconName(iconName)}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Showing {visibleIconNames.length} of {ALL_ICON_NAMES.length} icons. Search to narrow the list.
          </p>
        </div>
      )}
    </div>
  );
}
